import { Button, Group, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { describeRpcError, usePermission } from "@how/app-kit";
import { useFulfilOrder } from "../store/store-queries";
import { UndoPickupDialog } from "../store/UndoPickupDialog";
import { type Registration, type RegistrationAddOns } from "./event-queries";

/**
 * A registration's add-ons on the roster (ADR 0047 step 3): what was chosen, and whether it is unpaid or handed over.
 * Add-ons are handed over at the event, so a paid order carries **Hand over** here for anyone who fulfils orders
 * (#264), rather than only in Store → Orders, and a handed-over one carries **Undo** for a mistaken hand-over (#280).
 * Unpaid add-ons are not handed over until paid. `fulfil_order` and `unfulfil_order` check the permission themselves;
 * hiding the buttons is only the courtesy.
 */
export function RosterAddOns({ registration: r }: { readonly registration: Registration }) {
  const canHandOver = usePermission("commerce.fulfill_orders", { module: "commerce", write: true });
  // The same hand-over as Store → Orders, so both re-read the same things, after a refusal too (#277).
  const handOver = useFulfilOrder();
  const [undoing, setUndoing] = useState<RegistrationAddOns | null>(null);

  return (
    <>
      {r.addOns.map((a) => (
        // On a phone the button moves under the line rather than being squeezed; it keeps a 26px target (WCAG 2.5.8).
        <Group key={a.orderId} gap="xs" wrap="wrap" preventGrowOverflow={false}>
          <Text size="xs">
            Add-ons: {a.items}
            {a.status === "pending_payment" ? " (unpaid)" : ""}
            {a.status === "fulfilled" ? " (handed over)" : ""}
          </Text>
          {a.status === "paid" && canHandOver.data === true ? (
            <Button
              size="compact-sm"
              variant="default"
              style={{ flexShrink: 0 }}
              aria-label={`Hand over ${r.name}'s add-ons: ${a.items}`}
              loading={handOver.isPending && handOver.variables === a.orderId}
              onClick={() => {
                handOver.mutate(a.orderId, {
                  onSuccess: () => {
                    notifications.show({ message: `${r.name}'s add-ons are handed over.` });
                  },
                  onError: (error) => {
                    const described = describeRpcError(error);
                    notifications.show({
                      color: described.kind === "denied" ? "yellow" : "red",
                      title: "Not handed over",
                      message: described.message,
                    });
                  },
                });
              }}
            >
              Hand over
            </Button>
          ) : null}
          {a.status === "fulfilled" && canHandOver.data === true ? (
            <Button
              size="compact-sm"
              variant="subtle"
              style={{ flexShrink: 0 }}
              aria-label={`Undo hand-over of ${r.name}'s add-ons: ${a.items}`}
              onClick={() => {
                setUndoing(a);
              }}
            >
              Undo
            </Button>
          ) : null}
        </Group>
      ))}
      {undoing === null ? null : (
        <UndoPickupDialog
          title="Undo hand-over"
          orderId={undoing.orderId}
          orderNumber={undoing.number}
          onClose={() => {
            setUndoing(null);
          }}
          onUndone={() => {
            setUndoing(null);
            notifications.show({ message: `${r.name}'s add-ons are no longer handed over.` });
          }}
        />
      )}
    </>
  );
}
